import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import { PosCustomButton } from "../../../../customComponents/PosCustomButton/PosCustomButton";
import CustomTableGrid from "../../../../customComponents/CustomTableGrid/CustomTableGrid";
import CustomFilterComponent from "../../../../customComponents/CustomFilterComponent";
import { BuildingStorefrontIcon, TruckIcon, ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import "../index.css";
import "./fulfillment-kitchenOS.css";
import { MOCK_DISPATCH_QUEUE, MOCK_STORE_PICKUPS, MOCK_RECON_RECORDS, MOCK_LOCATION_OPTIONS } from "../MOCK_DATA_KOS";

const FILTER_CONFIG = [
  {
    key: "dispatchMode",
    label: "Dispatch Mode",
    options: [
      { label: "Direct Sale",   value: "Direct Sale"   },
      { label: "Stock Transfer", value: "Stock Transfer" },
      { label: "Consignment",   value: "Consignment"   },
      { label: "Periodic SO",   value: "Periodic SO"   },
      { label: "PO-Linked",     value: "PO-Linked"     },
    ],
  },
  {
    key: "status",
    label: "Status",
    options: [
      { label: "Ready",   value: "Ready"   },
      { label: "Pending", value: "Pending" },
    ],
  },
  {
    key: "destination",
    label: "Destination",
    options: MOCK_LOCATION_OPTIONS,
  },
];

const EMPTY_FILTERS = { dispatchMode: [], status: [], destination: [] };

const TABS = ["Dispatch Queue", "Store Pickups", "Day-Close Recon"];
const RECON_COUNT = MOCK_RECON_RECORDS?.length;

// ─── Badge Helpers ────────────────────────────────────────────────────────────

const MODE_SLUG = {
  "Direct Sale": "direct",
  "Stock Transfer": "stock",
  "Consignment": "consignment",
  "Periodic SO": "periodic",
  "PO-Linked": "po",
};

function DispatchModeBadge({ mode }) {
  return <span className={`f_kos_mode-badge f_kos_mode-${MODE_SLUG[mode] || "direct"}`}>{mode}</span>;
}
DispatchModeBadge.propTypes = { mode: PropTypes.string.isRequired };

function StatusBadge({ status }) {
  return <span className={`f_kos_status-badge f_kos_status-${status.toLowerCase()}`}>{status}</span>;
}
StatusBadge.propTypes = { status: PropTypes.string.isRequired };

function ReturnTypeBadge({ type }) {
  return <span className="f_kos_return-badge">{type}</span>;
}
ReturnTypeBadge.propTypes = { type: PropTypes.string.isRequired };

function VarianceBadge({ record }) {
  if (record.varianceType === "matched") {
    return <span className="f_kos_variance-matched">Matched</span>;
  }
  return (
    <span className="f_kos_variance-warning">
      <span className="f_kos_variance-label">{record.variance}</span>
      {record.varianceDetail && <span className="f_kos_variance-detail">{record.varianceDetail}</span>}
    </span>
  );
}
VarianceBadge.propTypes = { record: PropTypes.object.isRequired };

// ─── Column Definitions ───────────────────────────────────────────────────────

function makeDispatchColumns(selectedRows, onToggleRow) {
  return [
    {
      key: "select",
      label: "",
      render: (row) => (
        <input
          type="checkbox"
          className="f_kos_row-check"
          checked={selectedRows.has(row.id)}
          onChange={() => onToggleRow(row.id)}
          aria-label={`Select ${row.id}`}
        />
      ),
    },
    {
      key: "id",
      label: "DISPATCH ID",
      render: (row) => <span className="f_kos_id-link">{row.id}</span>,
    },
    {
      key: "destination",
      label: "DESTINATION",
      render: (row) => (
        <span className="f_kos_dest-cell">
          <BuildingStorefrontIcon className="f_kos_dest-icon" />
          {row.destination}
        </span>
      ),
    },
    {
      key: "totalItems",
      label: "TOTAL ITEMS",
      render: (row) => (
        <span className="f_kos_items-cell">
          <strong>{row.totalUnits}</strong> <span className="f_kos_items-unit">units</span>{" "}
          <span className="f_kos_items-sku">({row.totalSKUs} SKUs)</span>
        </span>
      ),
    },
    {
      key: "dispatchMode",
      label: "DISPATCH MODE",
      render: (row) => <DispatchModeBadge mode={row.dispatchMode} />,
    },
    {
      key: "status",
      label: "STATUS",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "financialImpact",
      label: "FINANCIAL IMPACT",
    },
    {
      key: "action",
      label: "ACTION",
      render: () => <button className="f_kos_action-link">Receive GRN</button>,
    },
  ];
}

function makePickupColumns(selectedPickups, onTogglePickup) {
  return [
    {
      key: "select",
      label: "",
      render: (row) => (
        <input
          type="checkbox"
          className="f_kos_row-check"
          checked={selectedPickups.has(row.id)}
          onChange={() => onTogglePickup(row.id)}
          aria-label={`Select ${row.id}`}
        />
      ),
    },
    {
      key: "id",
      label: "PICKUP ID",
      render: (row) => <span className="f_kos_id-link">{row.id}</span>,
    },
    {
      key: "sourceStore",
      label: "SOURCE STORE",
      render: (row) => (
        <span className="f_kos_dest-cell">
          <BuildingStorefrontIcon className="f_kos_dest-icon" />
          {row.sourceStore}
        </span>
      ),
    },
    {
      key: "totalItems",
      label: "TOTAL ITEMS",
      render: (row) => (
        <span className="f_kos_items-cell">
          <strong>{row.totalUnits}</strong> <span className="f_kos_items-unit">units</span>{" "}
          <span className="f_kos_items-sku">({row.totalSKUs} SKUs)</span>
        </span>
      ),
    },
    {
      key: "returnType",
      label: "RETURN TYPE",
      render: (row) => <ReturnTypeBadge type={row.returnType} />,
    },
    {
      key: "status",
      label: "STATUS",
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: "readyTime",
      label: "READY TIME",
    },
  ];
}

const RECON_COLUMNS = [
  {
    key: "outlet",
    label: "OUTLET",
    render: (row) => (
      <span className="f_kos_dest-cell">
        <BuildingStorefrontIcon className="f_kos_dest-icon" />
        {row.outlet}
      </span>
    ),
  },
  { key: "date", label: "DATE" },
  {
    key: "sentQty",
    label: "SENT QTY",
    render: (row) => <strong>{row.sentQty}</strong>,
  },
  {
    key: "returnedQty",
    label: "RETURNED QTY",
    render: (row) => <span className="f_kos_qty-red">{row.returnedQty}</span>,
  },
  {
    key: "impliedSales",
    label: "IMPLIED SALES",
    render: (row) => <span className="f_kos_qty-blue">{row.impliedSales}</span>,
  },
  {
    key: "variance",
    label: "VARIANCE",
    render: (row) => <VarianceBadge record={row} />,
  },
  {
    key: "action",
    label: "ACTION",
    render: () => <PosCustomButton title="Review & Confirm" variant="blue-d" p="6px 14px" fontSize="13px" />,
  },
];

// ─── Tab Views ────────────────────────────────────────────────────────────────

function DispatchQueueView({ data, selectedRows, onToggleRow }) {
  const columns = makeDispatchColumns(selectedRows, onToggleRow);
  return (
    <div className="f_kos_table-card">
      <div className="f_kos_table-scroll">
        <CustomTableGrid
          columns={columns}
          rows={data}
          rowKey="id"
          gridTemplateColumns="44px 130px minmax(160px, 1fr) 200px 150px 110px 160px 130px"
        />
      </div>
      {data?.length === 0 && (
        <p className="f_kos_no-results">No dispatches match the selected filters.</p>
      )}
    </div>
  );
}
DispatchQueueView.propTypes = {
  data: PropTypes.array.isRequired,
  selectedRows: PropTypes.instanceOf(Set).isRequired,
  onToggleRow: PropTypes.func.isRequired,
};

function StorePickupsView({ data, selectedPickups, onTogglePickup }) {
  const columns = makePickupColumns(selectedPickups, onTogglePickup);
  return (
    <div>
      <div className="f_kos_tab-actions">
        <PosCustomButton title="Batch Pickup" variant="blue-d" p="7px 16px" fontSize="13px" />
      </div>
      <div className="f_kos_table-card">
        <div className="f_kos_table-scroll">
          <CustomTableGrid
            columns={columns}
            rows={data}
            rowKey="id"
            gridTemplateColumns="44px 120px minmax(160px, 1fr) 200px 160px 110px 120px"
          />
        </div>
      </div>
    </div>
  );
}
StorePickupsView.propTypes = {
  data: PropTypes.array.isRequired,
  selectedPickups: PropTypes.instanceOf(Set).isRequired,
  onTogglePickup: PropTypes.func.isRequired,
};

function DayCloseReconView() {
  return (
    <div className="f_kos_recon-panel">
      <div className="f_kos_recon-panel-header">
        <ClipboardDocumentListIcon className="f_kos_recon-icon" />
        <div>
          <p className="f_kos_recon-title">Consignment Day-Close Reconciliation</p>
          <p className="f_kos_recon-subtitle">
            Review returned stock and calculate implied sales for consignment outlets. Sent - Returned = Implied Sales.
          </p>
        </div>
      </div>
      <div className="f_kos_table-scroll">
        <CustomTableGrid
          columns={RECON_COLUMNS}
          rows={MOCK_RECON_RECORDS}
          rowKey="id"
          gridTemplateColumns="200px 100px 120px 140px 150px 230px 180px"
        />
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function FulfillmentKitchenOS() {
  const [activeTab, setActiveTab] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(EMPTY_FILTERS);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectedPickups, setSelectedPickups] = useState(new Set());

  const activeFilterCount = useMemo(
    () => Object?.values(selectedFilters)?.reduce((sum, arr) => sum + arr?.length, 0),
    [selectedFilters]
  );

  const filteredDispatchQueue = useMemo(() => {
    const { dispatchMode, status, destination } = selectedFilters;
    return MOCK_DISPATCH_QUEUE?.filter((row) => {
      if (dispatchMode?.length > 0 && !dispatchMode?.includes(row.dispatchMode)) return false;
      if (status?.length > 0 && !status?.includes(row.status)) return false;
      if (destination?.length > 0 && !destination?.includes(row.destination)) return false;
      return true;
    });
  }, [selectedFilters]);

  function toggleRow(id) {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function togglePickup(id) {
    setSelectedPickups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  return (
    <Navbar pageTitle="Kitchen OS">
      <div className="idx_kos_wrapper">
        <div className="f_kos_page">
          {/* Header */}
          <div className="f_kos_header">
            <div>
              <div className="f_kos_header-title-row">
                <TruckIcon className="f_kos_header-icon" />
                <p className="f_kos_title">Fulfilment &amp; Dispatch</p>
              </div>
              <p className="f_kos_subtitle">Manage outbound logistics, inter-org billing, and stock transfers.</p>
            </div>
            <div className="f_kos_header-actions">
              <div className="f_kos_filter-btn-wrapper">
                <PosCustomButton
                  title="Filter"
                  variant="black-s"
                  p="7px 14px"
                  fontSize="13px"
                  border
                  onClickFunction={() => setFilterOpen(true)}
                />
                {activeFilterCount > 0 && (
                  <span className="f_kos_filter-badge">{activeFilterCount}</span>
                )}
              </div>
              <PosCustomButton title="Batch Dispatch" variant="blue-d" p="7px 16px" fontSize="13px" />
            </div>
          </div>

          {/* Tabs */}
          <div className="f_kos_tabs">
            {TABS?.map((tab, i) => (
              <button
                key={tab}
                className={`f_kos_tab ${activeTab === i ? "f_kos_tab-active" : ""}`}
                onClick={() => setActiveTab(i)}
              >
                {tab}
                {i === 2 && <span className="f_kos_tab-count">{RECON_COUNT}</span>}
              </button>
            ))}
          </div>

          {/* Content */}
          {activeTab === 0 && (
            <DispatchQueueView
              data={filteredDispatchQueue}
              selectedRows={selectedRows}
              onToggleRow={toggleRow}
            />
          )}
          {activeTab === 1 && (
            <StorePickupsView
              data={MOCK_STORE_PICKUPS}
              selectedPickups={selectedPickups}
              onTogglePickup={togglePickup}
            />
          )}
          {activeTab === 2 && <DayCloseReconView />}
        </div>
      </div>

      <CustomFilterComponent
        open={filterOpen}
        handleClose={() => setFilterOpen(false)}
        filterConfig={FILTER_CONFIG}
        selectedFilters={selectedFilters}
        onApplyFilter={setSelectedFilters}
        variant="panel"
      />
    </Navbar>
  );
}
